import { useQuery } from '@apollo/client'
import { useUrlSearchParams } from '@dai/common'
import { Box } from '@mui/material'
import { round, uniq } from 'lodash'

import { FeatureMessage } from '@/components/common/UpgradeMessage'
import { useRefresh } from '@/contexts/RefreshContext'
import {
  GET_POS_INTERVAL_STATS,
  GetPosIntervalStatsQuery,
  GetPosIntervalStatsQueryVariables,
  GroupByDate,
  POSAmountType,
} from '@/graphql'
import { formatCurrency } from '@/i18n/tolgee'
import useT from '@/i18n/useT'
import DAIOverTimeLineChart, {
  DAIOverTimeLineChartSeries,
} from '@/pages/charts/ApexCharts/DAIOverTimeLineChart'
import { ChartHelpers } from '@/utils/chart.helpers'
import { DateHelpers } from '@/utils/date.helpers'

type Props = {
  startDate: string
  endDate: string
  storeIds?: string[]
  insideCategories?: string[]
  outsideCategories?: string[]
  annotationStartDate?: string
  annotationEndDate?: string
  needToSetUpCategories: boolean
}

export const InsideVsOutsideSalesChart: React.FC<Props> = props => {
  const {
    storeIds,
    startDate,
    endDate,
    insideCategories,
    outsideCategories,
    annotationStartDate,
    annotationEndDate,
    needToSetUpCategories,
  } = props
  const { t } = useT()

  const {
    handle: { handleNavTo },
  } = useUrlSearchParams()

  const {
    data: insideData,
    refetch: refetchInside,
    loading: insideLoading,
  } = useQuery<GetPosIntervalStatsQuery, GetPosIntervalStatsQueryVariables>(
    GET_POS_INTERVAL_STATS,
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        filters: {
          storeIds,
          amountType: POSAmountType.SALE_PRICE,
          startDate,
          endDate,
          categories: insideCategories,
        },
      },
    },
  )

  const {
    data: outsideData,
    refetch: refetchOutside,
    loading: outsideLoading,
  } = useQuery<GetPosIntervalStatsQuery, GetPosIntervalStatsQueryVariables>(
    GET_POS_INTERVAL_STATS,
    {
      variables: {
        filters: {
          storeIds,
          amountType: POSAmountType.SALE_PRICE,
          startDate,
          endDate,
          categories: outsideCategories,
        },
      },
    },
  )

  const handleRefresh = () => {
    refetchInside()
    refetchOutside()
  }

  useRefresh(handleRefresh)

  const getLabels = (): string[] => {
    const filtered = (insideData?.posIntervalStats.results
      .filter(stat => !!stat.date && !!stat.label)
      .map(s => s.date) || []) as string[]
    return uniq(filtered)
  }

  const getSeries = (): DAIOverTimeLineChartSeries[] => {
    const insideStats = (insideData?.posIntervalStats.results || [])
      .filter(s => !!s.label)
      .map(stat => round(stat.value || 0))
    const outsideStats = (outsideData?.posIntervalStats.results || [])
      .filter(s => !!s.label)
      .map(stat => round(stat.value || 0))
    const totals = insideStats.map((insideStat, index) => {
      const outsideStat = outsideStats[index] || 0
      return round((insideStat || 0) + outsideStat)
    })
    return [
      { id: 'inside', name: 'Inside', data: insideStats, dataType: 'currency' },
      {
        id: 'outside',
        name: 'Outside',
        data: outsideStats,
        dataType: 'currency',
      },
      { id: 'total', name: 'Total', data: totals, dataType: 'currency' },
    ]
  }

  return (
    <DAIOverTimeLineChart
      title={t('revenueDetails.insideVsOutside')}
      series={getSeries()}
      dates={getLabels().map(date =>
        DateHelpers.parseIsoDateWithoutTimezone(date),
      )}
      dataGranularity={insideData?.posIntervalStats?.period || GroupByDate.DAY}
      options={{
        stroke: { curve: 'smooth' },
        yaxis: {
          labels: {
            formatter: val => (val ? formatCurrency(val) : ''),
          },
        },
        annotations: {
          xaxis: ChartHelpers.getXAxisRangeAnnotation(
            t,
            annotationStartDate,
            annotationEndDate,
            insideData?.posIntervalStats?.period || GroupByDate.DAY,
          ),
        },
      }}
      loading={insideLoading || outsideLoading}
      Overlay={
        needToSetUpCategories && (
          <Box
            sx={{
              borderRadius: 3,
              backgroundColor: 'background.paper',
            }}
          >
            <FeatureMessage
              message={t('setUpCategories.message')}
              buttonText={t('setUpCategories.button')}
              onClick={() => handleNavTo('/admin/sales-data')}
            />
          </Box>
        )
      }
    />
  )
}
